import { useAppSelector } from "app/store";
import { getDataReportTypes } from "pages/data/DataConfig";
import { useAllTeams } from "hooks/useSubTeams";
import { Team } from "models/schemas";

/**
 * Whether the caller is a critical user of the given team, read from listTeams() — the same source the
 * server resolves its write gate from, so the two agree.
 *
 * Deliberately not `state.teamSettings.isUserCriticalInTeam`: that is only ever set for the
 * header-selected team, so it answers the wrong question for a sub-team, and the per-team map beside it
 * is never populated.
 */
export const useIsCriticalInTeam = (teamName?: string): boolean => {
  const allTeams = useAllTeams();
  if (!teamName) {
    return false;
  }
  return (
    allTeams.find((team: Team) => team.teamId === teamName)?.isCritical ?? false
  );
};

export const useSelectedTeam = () => {
  const selectedTeam = useAppSelector(
    (state) => state.teamSettings.selectedTeamMainMetaData,
  );

  const isFileScanEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_SCAN") ?? false;

  const isFileScanAssessmentEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_SCAN_ASSESSMENT") ?? false;

  const isFileScanTaxAreaEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_SCAN_TAX_AREA") ?? false;

  const isFileScanPaymentEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_SCAN_PAYMENT") ?? false;

  const isSellerInfoSearchWorkflowEnabled =
    selectedTeam?.enabledFeatures?.includes(
      "SELLER_INFORMATION_LOOKUP_WORKFLOW",
    ) ?? false;

  const isBulkUploadAuditsEnabled =
    selectedTeam?.enabledFeatures?.includes("AUDIT") ?? false;

  const isBulkUploadNoticesEnabled =
    selectedTeam?.enabledFeatures?.includes("NOTICE") ?? false;

  const isBulkUploadLitigationsEnabled =
    selectedTeam?.enabledFeatures?.includes("LITIGATION") ?? false;

  const isRollForwardEnabled =
    selectedTeam?.enabledFeatures?.includes("ROLL_FORWARD") ?? false;

  const isAssistantEnabled =
    selectedTeam?.enabledFeatures?.includes("ASSISTANT") ?? false;

  const isSupersededPaymentsEnabled =
    selectedTeam?.enabledFeatures?.includes("SUPERSEDED_PAYMENTS") ?? false;

  const isGaiaObjectsCriticalDeleteOnlyEnabled =
    selectedTeam?.enabledFeatures?.includes(
      "GAIAOBJECTS_CRITICAL_DELETE_ONLY",
    ) ?? false;

  const isGaiaKBIngestionEnabled =
    selectedTeam?.enabledFeatures?.includes("GAIA_KB_INGESTION") ?? false;

  const isKBApprovalEnabled =
    selectedTeam?.enabledFeatures?.includes("KB_APPROVAL") ?? false;

  const isKBApprovalDeletionEnabled =
    selectedTeam?.enabledFeatures?.includes("KB_APPROVAL_DELETION") ?? false;

  const isKBApprovalFilesEnabled =
    selectedTeam?.enabledFeatures?.includes("KB_APPROVAL_FILES") ?? false;

  const isFolderKBIngestionEnabled =
    selectedTeam?.enabledFeatures?.includes("FOLDER_KB_INGESTION") ?? false;

  const isAnswersKbEnabled =
    selectedTeam?.enabledFeatures?.includes("CHATBOT") ?? false;

  const isFileDescriptionEnabled =
    selectedTeam?.enabledFeatures?.includes("FILE_DESCRIPTION") ?? false;

  const isKBTagEnabled =
    selectedTeam?.enabledFeatures?.includes("KB_TAG") ?? false;

  const isDataInsightsEnabled =
    selectedTeam?.enabledFeatures?.includes("DATA_INSIGHTS") ?? false;

  const isAgentWorkEnabled =
    selectedTeam?.enabledFeatures?.includes("AGENT_WORK") ?? false;

  const isHomePageEnabled =
    selectedTeam?.enabledFeatures?.includes("HOME_PAGE") ?? false;

  const isAssignmentGroupsEnabled =
    selectedTeam?.enabledFeatures?.includes("ASSIGNMENT_GROUPS") ?? false;

  return {
    selectedTeam,
    isFileScanEnabled,
    isFileScanAssessmentEnabled,
    isFileScanTaxAreaEnabled,
    isFileScanPaymentEnabled,
    isSellerInfoSearchWorkflowEnabled,
    isBulkUploadAuditsEnabled,
    isBulkUploadNoticesEnabled,
    isBulkUploadLitigationsEnabled,
    isRollForwardEnabled,
    isAssistantEnabled,
    isSupersededPaymentsEnabled,
    isGaiaObjectsCriticalDeleteOnlyEnabled,
    isGaiaKBIngestionEnabled,
    isKBApprovalEnabled,
    isKBApprovalDeletionEnabled,
    isKBApprovalFilesEnabled,
    isFolderKBIngestionEnabled,
    isAnswersKbEnabled,
    isFileDescriptionEnabled,
    isKBTagEnabled,
    isDataInsightsEnabled,
    isAgentWorkEnabled,
    isHomePageEnabled,
    isAssignmentGroupsEnabled,
  };
};

export const useWorkflowAvailability = () => {
  const teamName = useAppSelector((state) => state.audits.teamName);
  const headerSelectedTeam = useAppSelector(
    (state) => state.audits.headerSelectedTeam,
  );
  const isCriticalUser = useAppSelector(
    (state) => state.teamSettings.isUserCriticalInTeam,
  );
  const {
    isSellerInfoSearchWorkflowEnabled,
    isBulkUploadAuditsEnabled,
    isBulkUploadNoticesEnabled,
    isBulkUploadLitigationsEnabled,
  } = useSelectedTeam();

  const hasWorkflowsAvailable = (parentId?: string) => {
    if (headerSelectedTeam === "guest") {
      return false;
    }
    const workflows = getDataReportTypes(
      teamName,
      isCriticalUser,
      parentId,
      isSellerInfoSearchWorkflowEnabled,
      isBulkUploadAuditsEnabled,
      isBulkUploadNoticesEnabled,
      isBulkUploadLitigationsEnabled,
    );
    return workflows.length > 0;
  };

  return {
    hasWorkflowsAvailable,
  };
};

const CATEGORY_ALIASES: Record<string, string> = {
  audits: "audit",
  notices: "notice",
  litigations: "litigation",
  "team-files": "team",
};

/**
 * Gaia default document types for audit draft, notice draft, litigation draft,
 * payment files, assessment files, event files, and team files
 * Audit files and Task files can select any team document type they do not have default
 */
const GAIA_DEFAULTS: Record<string, string> = {
  audit: "Audit notices",
  notice: "Notice letters",
  litigation: "Notices",
  payments: "Payments",
  assessments: "Assessments",
  events: "Events",
  team: "Team",
};

export const useDefaultDocumentType = (category: string): string => {
  const defaultDocumentTypes = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData?.defaultDocumentTypes,
  );
  const normalized = CATEGORY_ALIASES[category] ?? category;
  return (
    defaultDocumentTypes?.[normalized] ?? GAIA_DEFAULTS[normalized] ?? category
  );
};
